import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { requestPayout } from '../../../services/earningsService';

const PayoutStatus = ({ 
  payouts = [], 
  nextPayout, 
  withdrawalMethods = [],
  onRefresh,
  fullView = false 
}) => {
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'processing':
        return 'bg-primary/10 text-primary';
      case 'failed': case'cancelled':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'processing':
        return 'Loader';
      case 'failed': case'cancelled':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-PH', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleRequestPayout = async () => {
    try {
      setLoading(true);
      setError('');

      if (!requestAmount || parseFloat(requestAmount) <= 0) {
        setError('Please enter a valid amount');
        return;
      }

      if (!selectedMethod) {
        setError('Please select a withdrawal method');
        return;
      }

      await requestPayout({
        amount: parseFloat(requestAmount),
        withdrawalMethodId: selectedMethod
      });

      setShowRequestModal(false);
      setRequestAmount('');
      setSelectedMethod('');
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Error requesting payout:', err);
      setError(err?.message || 'Failed to request payout');
    } finally {
      setLoading(false);
    }
  };

  const verifiedMethods = withdrawalMethods?.filter(m => m?.status === 'verified') || [];

  return (
    <div className="bg-card rounded-lg p-4 md:p-6 shadow-warm">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-1">
            Payout Status
          </h2>
          <p className="text-sm text-muted-foreground">
            Track your withdrawal requests and payout schedule
          </p>
        </div>
        <Button
          size="sm"
          iconName="Send"
          onClick={() => setShowRequestModal(true)}
          disabled={verifiedMethods?.length === 0}
        >
          Request Payout
        </Button>
      </div>
      {nextPayout && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Calendar" size={20} className="text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-foreground mb-1">Next Scheduled Payout</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Requested on {formatDate(nextPayout?.requestedAt)}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-primary">
                  ₱{parseFloat(nextPayout?.amount || 0)?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                </span>
                <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(nextPayout?.status)}`}>
                  {nextPayout?.status}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {payouts?.slice(0, fullView ? undefined : 5)?.map((payout) => (
          <div
            key={payout?.id}
            className="flex items-center justify-between p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-10 h-10 rounded-lg bg-card flex items-center justify-center`}>
                <Icon name={getStatusIcon(payout?.status)} size={18} className={getStatusColor(payout?.status)?.replace('bg-', 'text-')?.split(' ')?.[0]} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground">
                  ₱{parseFloat(payout?.amount || 0)?.toLocaleString('en-PH', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatDate(payout?.requestedAt)}
                  {payout?.withdrawalMethod && (
                    <> • {payout?.withdrawalMethod?.methodType?.replace('_', ' ')}</>  
                  )}
                </p>
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(payout?.status)}`}>
              {payout?.status}
            </span>
          </div>
        ))}

        {payouts?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Send" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No payout requests yet</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-4"
              onClick={() => setShowRequestModal(true)}
              disabled={verifiedMethods?.length === 0}
            >
              Request Your First Payout
            </Button>
          </div>
        )}
      </div>
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-foreground">Request Payout</h3>
              <button
                onClick={() => {
                  setShowRequestModal(false);
                  setError('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 mb-4">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {verifiedMethods?.length === 0 ? (
              <div className="text-center py-6">
                <Icon name="AlertCircle" size={48} className="text-warning mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">
                  You need to add and verify a withdrawal method first
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowRequestModal(false)}
                >
                  Go to Withdrawal Methods
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Input
                  type="number"
                  label="Amount (PHP)"
                  placeholder="Enter amount"
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(e?.target?.value)}
                  min="100"
                  step="0.01"
                />

                <Select
                  label="Withdrawal Method"
                  value={selectedMethod}
                  onChange={(e) => setSelectedMethod(e?.target?.value)}
                >
                  <option value="">Select method</option>
                  {verifiedMethods?.map((method) => (
                    <option key={method?.id} value={method?.id}>
                      {method?.methodType?.replace('_', ' ')} - {method?.accountNumber}
                      {method?.isPrimary && ' (Primary)'}
                    </option>
                  ))}
                </Select>

                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground">
                    <Icon name="Info" size={14} className="inline mr-1" />
                    Payouts are typically processed within 1-3 business days
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button
                    fullWidth
                    onClick={handleRequestPayout}
                    loading={loading}
                    disabled={loading}
                  >
                    Request Payout
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRequestModal(false);
                      setError('');
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutStatus;